import React, { useCallback, useContext, useEffect, useState } from 'react';
import stringSimilarity from 'string-similarity';
import * as Styled from './BranchingOptions.styled';
import { useDispatch, useSelector } from 'react-redux';
import {
  ChatStates,
  isIteratingBranchingNodesSelector,
  iterateBranchingNodes,
  selectChatState,
  selectChatStatus,
  sendBranchingOption,
  virtualHumanTalkingSelector,
  waitingOnUserContinueSelector,
} from '../../../../../slices/chat/ChatSlice';
import { virtualHumanDataSelector } from '../../../../../slices/VirtualHumanSlice';
import { languageSelector } from '../../../../../slices/PatientSlice';
import useDeepgramDirect from '../../../../../utility/useDeepgramDirect';
import { usePrevious } from '../../../../../../../../utility/CustomHooks';
import { StripSpecialChars } from '../../../../../utility/StringUtil';
import { MicMutedContext } from '../../../../VHChatInputArea/MicMutedProvider.context';
import NoMatchesNotification from '@vh/components/vh-types/branching/BranchingInputArea/BranchingOptions/NoMatchesNotification/NoMatchesNotification';
import RecordingButton from '@vh/components/VHChatInputArea/RecordingButton/RecordingButton';
import MicBehaviorSelector from '@vh/components/VHChatInputArea/MicBehaviorSelector/MicBehaviorSelector';
import { MicBehaviorContext } from '@vh/components/VHChatInputArea/MicBehaviorProvider.context';
import { useDebounce } from 'use-debounce';
import { useDisconnectNotify } from '@vh/utility/useDisconnectNotify';
import { isForceDisabledSelector } from '@vh/slices/vh-player/VHPlayerSlice';

const BranchingOptions = () => {
  const { muted, setMuted } = useContext(MicMutedContext);
  const waitingOnUserContinue = useSelector(waitingOnUserContinueSelector);
  const chatState = useSelector(selectChatState);
  const chatStatus = useSelector(selectChatStatus);
  const virtualHumanTalking = useSelector(virtualHumanTalkingSelector);
  const dispatch = useDispatch();
  const vh = useSelector(virtualHumanDataSelector);
  const selectableBranchingOptions = useSelector(
    (state) => state.chat.selectableBranchingOptions
  );
  const isForceDisabled = useSelector(isForceDisabledSelector);

  const chatLoadingStatus = useSelector((state) => state.chat.status);

  const showMutedNoticeIfTalked =
    muted &&
    !virtualHumanTalking &&
    chatLoadingStatus !== 'pending' &&
    chatState !== ChatStates.LOADING;
  const pausedTranscriptions = muted || virtualHumanTalking;
  const optionsDisabled =
    chatStatus !== 'idle' ||
    chatState === ChatStates.LOADING ||
    chatLoadingStatus === 'pending' ||
    isForceDisabled ||
    virtualHumanTalking;

  const continueDisabled =
    chatStatus !== 'idle' ||
    chatState === ChatStates.LOADING ||
    isForceDisabled ||
    virtualHumanTalking;

  const isIteratingBranchingNodes = useSelector(
    isIteratingBranchingNodesSelector
  );
  const submitBranchingResponse = useCallback(
    (branchingOption) => {
      setNoMatchesFound(false);
      dispatch(
        sendBranchingOption({
          branchingOption,
          vhId: vh.ExternalID,
        })
      );
    },
    [dispatch, vh.ExternalID]
  );

  const language = useSelector(languageSelector);
  const [noMatchesFound, setNoMatchesFound] = useState(false);

  const pauseDeepgram =
    muted ||
    virtualHumanTalking ||
    chatLoadingStatus === 'pending' ||
    chatState === ChatStates.LOADING;
  const [debouncedPauseDeepgram] = useDebounce(pauseDeepgram, 50);

  const { incrementDisconnects } = useDisconnectNotify();
  const handleCloseSocket = useCallback(
    (closeCode, closeReason) => {
      if (closeCode !== 1000) {
        setMuted(true);
        if (closeReason) {
          incrementDisconnects(closeReason);
        }
      }
    },
    [incrementDisconnects, setMuted]
  );

  const {
    currentUnfinishedAffirmation,
    currentAffirmation,
    finalAffirmation,
    error,
    connected,
    hasAttemptedConnection,
    reconnect,
    connecting,
  } = useDeepgramDirect({
    language,
    paused: debouncedPauseDeepgram,
    onClose: handleCloseSocket,
  });

  const recordingDisabled =
    chatState === ChatStates.LOADING ||
    chatState === ChatStates.COMPLETE ||
    isForceDisabled ||
    chatStatus === 'pending' ||
    virtualHumanTalking ||
    isIteratingBranchingNodes;

  const prevFinalAffirmation = usePrevious(finalAffirmation);

  // check if user says continue
  useEffect(() => {
    if (
      !pausedTranscriptions &&
      finalAffirmation !== prevFinalAffirmation &&
      !muted &&
      !virtualHumanTalking &&
      waitingOnUserContinue
    ) {
      let saidWords = StripSpecialChars(finalAffirmation).toLowerCase();
      if (saidWords.indexOf('continue') !== -1) {
        dispatch(iterateBranchingNodes());
      }
    }
  }, [
    finalAffirmation,
    dispatch,
    muted,
    pausedTranscriptions,
    prevFinalAffirmation,
    virtualHumanTalking,
    waitingOnUserContinue,
  ]);

  const { micBehavior } = useContext(MicBehaviorContext);

  console.log(currentUnfinishedAffirmation);

  // if mic stops listening and a transcript exists, submit that newly spoken text
  useEffect(() => {
    if (
      !pausedTranscriptions &&
      finalAffirmation !== prevFinalAffirmation &&
      finalAffirmation &&
      !muted &&
      !virtualHumanTalking &&
      selectableBranchingOptions.length > 0 &&
      !waitingOnUserContinue
    ) {
      let saidWords = StripSpecialChars(finalAffirmation).toLowerCase();
      const optionTexts = selectableBranchingOptions.map((option) => {
        return StripSpecialChars(option.text).toLowerCase();
      });
      // use string similarity rather than a fuzzy library cos we just need closest match
      const matches = stringSimilarity.findBestMatch(saidWords, optionTexts);
      const matchedOption = selectableBranchingOptions[matches.bestMatchIndex];
      // if the best matched option
      if (matches.bestMatch.rating > 0.4) {
        setNoMatchesFound(false);
        dispatch(
          sendBranchingOption({
            branchingOption: matchedOption,
            vhId: vh.ExternalID,
          })
        );
      } else {
        setNoMatchesFound(true);
      }
    }
  }, [
    finalAffirmation,
    virtualHumanTalking,
    prevFinalAffirmation,
    muted,
    dispatch,
    pausedTranscriptions,
    selectableBranchingOptions,
    vh?.ExternalID,
    waitingOnUserContinue,
  ]);

  const handleNoMatchesShown = useCallback(() => {
    setNoMatchesFound(false);
  }, []);
  // the actual recording state (where muted is the overall muted desirability state, i.e. stays true if we're waiting for input to be available again)
  const [recording, setRecording] = useState(false);

  const handleRecordingButtonClick = useCallback(() => {
    if (recordingDisabled) {
      return;
    }
    if (recording) {
      setRecording(false);
      setMuted(true);
    } else {
      setRecording(true);
      setMuted(false);
      if (!connected) {
        reconnect();
      }
    }
  }, [
    setRecording,
    setMuted,
    recording,
    connected,
    reconnect,
    recordingDisabled,
  ]);

  useEffect(() => {
    if (debouncedPauseDeepgram) {
      setRecording(false);
    }
  }, [debouncedPauseDeepgram]);

  return (
    <>
      {chatState !== ChatStates.COMPLETE && (
        <Styled.InputOptionsWrapper>
          <Styled.InputOptionsSubwrapper>
            {waitingOnUserContinue && (
              <Styled.InputOption
                onClick={() => dispatch(iterateBranchingNodes())}
                disabled={continueDisabled}
              >
                Continue
              </Styled.InputOption>
            )}
            {!waitingOnUserContinue &&
              !virtualHumanTalking &&
              chatState === ChatStates.TALK &&
              selectableBranchingOptions.map((selectableBranchingOption) => {
                return (
                  <Styled.InputOption
                    key={selectableBranchingOption.id}
                    onClick={() =>
                      submitBranchingResponse(selectableBranchingOption)
                    }
                    disabled={optionsDisabled}
                  >
                    {selectableBranchingOption.text}
                    <Styled.SendIcon icon="arrow-up" type="solid" />
                  </Styled.InputOption>
                );
              })}
          </Styled.InputOptionsSubwrapper>
        </Styled.InputOptionsWrapper>
      )}
      <Styled.ButtonsWrapper disabled={chatState === ChatStates.LOADING}>
        <>
          {micBehavior === 'open' ? (
            <RecordingButton
              recording={false}
              disabled={recordingDisabled}
              onClick={handleRecordingButtonClick}
              muted={muted}
              connected={connected}
              showMutedNoticeIfTalked={showMutedNoticeIfTalked}
              networkError={!connected && error}
              connecting={connecting}
            />
          ) : (
            <RecordingButton
              disabled={recordingDisabled}
              onClick={handleRecordingButtonClick}
              muted={muted}
              connected={connected}
              showMutedNoticeIfTalked={showMutedNoticeIfTalked}
              networkError={!connected && error}
              recording={recording}
              connecting={connecting}
            />
          )}
          <MicBehaviorSelector
            solidButton={true}
            disabled={recordingDisabled}
          />
        </>
      </Styled.ButtonsWrapper>
      <NoMatchesNotification
        onShown={handleNoMatchesShown}
        active={noMatchesFound}
      />
    </>
  );
};

export default BranchingOptions;
